import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { format } from "date-fns";
import { toast } from "sonner";
import { Check, Loader2, ChevronLeft, CalendarDays, Clock, Shield } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";

const steps = ["Service", "Date & Time", "Your Details"];

export default function Booking() {
  const [services, setServices] = useState([]);
  const [step, setStep] = useState(0);
  const [category, setCategory] = useState("auto");
  const [service, setService] = useState(null);
  const [date, setDate] = useState(null);
  const [slot, setSlot] = useState(null);
  const [slots, setSlots] = useState({ available: [], all_slots: [], booked: [] });
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data.services)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!date) return;
    setLoadingSlots(true);
    setSlot(null);
    const ds = format(date, "yyyy-MM-dd");
    api.get(`/availability?date=${ds}`)
      .then((r) => setSlots(r.data))
      .catch(() => setSlots({ available: [], all_slots: [], booked: [] }))
      .finally(() => setLoadingSlots(false));
  }, [date]);

  const filtered = services.filter((s) => s.category === category);

  const next = () => {
    if (step === 0 && !service) return toast.error("Please select a service");
    if (step === 1 && (!date || !slot)) return toast.error("Please pick a date and time");
    setStep((s) => Math.min(s + 1, 2));
  };

  const submit = async () => {
    if (!form.name || !form.email || !form.phone) return toast.error("Please fill in your name, email and phone");
    setSubmitting(true);
    try {
      const { data } = await api.post("/bookings", {
        category, service_id: service.id, service_name: service.name,
        date: format(date, "yyyy-MM-dd"), time_slot: slot,
        name: form.name, email: form.email, phone: form.phone, notes: form.notes,
        origin_url: window.location.origin,
      });
      toast.success("Redirecting to secure payment…");
      // Persist the last booking session for context on success page
      try {
        sessionStorage.setItem("last_booking", JSON.stringify({
          service_name: service.name, date: format(date, "EEEE, MMM d"), time_slot: slot, name: form.name,
        }));
      } catch {}
      window.location.href = data.checkout_url;
    } catch (e) {
      const msg = e?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(msg);
      setSubmitting(false);
    }
  };

  return (
    <section id="book" className="py-24 lg:py-32 bg-secondary/40 border-y border-border" data-testid="booking-section">
      <div className="max-w-[1100px] mx-auto px-6 lg:px-10">
        <div className="text-center mb-14">
          <p className="text-xs tracking-[0.3em] uppercase text-primary mb-4">Reserve your slot</p>
          <h2 className="font-serif text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-none text-foreground">
            Book your <span className="italic text-primary">detail.</span>
          </h2>
          <p className="text-muted-foreground font-light mt-4 max-w-lg mx-auto text-sm">
            Secure your slot with a small non-refundable deposit. Applied to your final bill.
          </p>
        </div>

        <div className="bg-card border gold-border clip-frame p-6 sm:p-10 lg:p-14">
          <div className="flex items-center justify-center gap-4 sm:gap-8 mb-12">
            {steps.map((label, i) => (
              <div key={label} className="flex items-center gap-3">
                <div className={`w-8 h-8 flex items-center justify-center text-xs border transition-colors ${i <= step ? "border-primary text-primary" : "border-border text-muted-foreground"}`}>
                  {i + 1}
                </div>
                <span className={`text-sm tracking-wide hidden sm:block ${i <= step ? "text-foreground" : "text-muted-foreground"}`}>{label}</span>
                {i < steps.length - 1 && <span className="w-6 sm:w-12 h-px bg-border" />}
              </div>
            ))}
          </div>

          <AnimatePresence mode="wait">
            {step === 0 && (
              <motion.div key="s0" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} data-testid="booking-step-service">
                <div className="flex gap-3 mb-8 justify-center">
                  {["auto", "home"].map((c) => (
                    <button key={c} onClick={() => { setCategory(c); setService(null); }} data-testid={`booking-cat-${c}`}
                      className={`px-8 py-3 text-sm tracking-wide clip-frame transition-colors ${category === c ? "bg-primary text-primary-foreground" : "border border-border text-muted-foreground hover:text-foreground"}`}>
                      {c === "auto" ? "Auto" : "Home"}
                    </button>
                  ))}
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  {filtered.map((s) => (
                    <button key={s.id} onClick={() => setService(s)} data-testid={`booking-service-${s.id}`}
                      className={`text-left p-6 border clip-frame transition-colors ${service?.id === s.id ? "border-primary bg-primary/5" : "border-border hover:border-primary/40"}`}>
                      <div className="flex justify-between items-start mb-2">
                        <h4 className="font-serif text-2xl text-foreground">{s.name}</h4>
                        {service?.id === s.id && <Check className="w-5 h-5 text-primary" />}
                      </div>
                      <p className="text-sm text-muted-foreground font-light mb-3 line-clamp-2">{s.description}</p>
                      <div className="flex items-baseline justify-between">
                        <span className="text-primary font-serif text-lg">{s.price}</span>
                        <span className="text-xs tracking-wide text-muted-foreground">Deposit {s.deposit_display}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </motion.div>
            )}

            {step === 1 && (
              <motion.div key="s1" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="grid lg:grid-cols-2 gap-10" data-testid="booking-step-datetime">
                <div>
                  <p className="flex items-center gap-2 text-sm tracking-wide text-foreground mb-4"><CalendarDays className="w-4 h-4 text-primary" /> Select a date</p>
                  <div className="flex justify-center bg-background border border-border clip-frame p-2">
                    <Calendar mode="single" selected={date} onSelect={setDate} disabled={(d) => d < new Date(new Date().setHours(0,0,0,0))} data-testid="booking-calendar" />
                  </div>
                </div>
                <div>
                  <p className="flex items-center gap-2 text-sm tracking-wide text-foreground mb-4"><Clock className="w-4 h-4 text-primary" /> Select a time</p>
                  {!date ? (
                    <p className="text-muted-foreground font-light text-sm">Choose a date to see available times.</p>
                  ) : loadingSlots ? (
                    <div className="flex items-center gap-2 text-muted-foreground text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Loading times…</div>
                  ) : (
                    <div className="grid grid-cols-2 gap-3">
                      {slots.all_slots.map((t) => {
                        const avail = slots.available.includes(t);
                        return (
                          <button key={t} disabled={!avail} onClick={() => setSlot(t)} data-testid={`booking-slot-${t.replace(/[:\s]/g, "")}`}
                            className={`py-3 text-sm tracking-wide clip-frame border transition-colors ${slot === t ? "bg-primary text-primary-foreground border-primary" : avail ? "border-border text-foreground hover:border-primary/50" : "border-border/50 text-muted-foreground/40 line-through cursor-not-allowed"}`}>
                            {t}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </motion.div>
            )}

            {step === 2 && (
              <motion.div key="s2" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="max-w-xl mx-auto" data-testid="booking-step-details">
                <div className="mb-6 p-4 border border-border clip-frame text-sm text-muted-foreground flex flex-wrap gap-x-6 gap-y-1">
                  <span><span className="text-primary">Service:</span> {service?.name}</span>
                  <span><span className="text-primary">When:</span> {date && format(date, "MMM d")} · {slot}</span>
                  <span><span className="text-primary">Deposit:</span> {service?.deposit_display}</span>
                </div>
                <div className="grid gap-4">
                  <Input placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="booking-name" className="bg-background border-border h-12" />
                  <div className="grid sm:grid-cols-2 gap-4">
                    <Input placeholder="Email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="booking-email" className="bg-background border-border h-12" />
                    <Input placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="booking-phone" className="bg-background border-border h-12" />
                  </div>
                  <Textarea placeholder="Vehicle / home details, special requests…" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="booking-notes" className="bg-background border-border min-h-28" />
                </div>
                <p className="mt-6 flex items-start gap-2 text-xs text-muted-foreground leading-relaxed">
                  <Shield className="w-3.5 h-3.5 text-primary shrink-0 mt-0.5" />
                  Secure checkout via Stripe. Your {service?.deposit_display} deposit is non-refundable if you no-show or cancel late, and is applied toward your final bill on the day of service.
                </p>
              </motion.div>
            )}
          </AnimatePresence>

          <div className="flex items-center justify-between mt-12">
            <button onClick={() => setStep((s) => Math.max(s - 1, 0))} disabled={step === 0} data-testid="booking-back-btn"
              className={`flex items-center gap-1 text-sm tracking-wide transition-colors ${step === 0 ? "text-muted-foreground/30 cursor-not-allowed" : "text-muted-foreground hover:text-foreground"}`}>
              <ChevronLeft className="w-4 h-4" /> Back
            </button>
            {step < 2 ? (
              <button onClick={next} data-testid="booking-next-btn" className="px-10 py-3.5 bg-primary text-primary-foreground text-sm tracking-wide hover:bg-primary/90 transition-colors clip-frame">
                Continue
              </button>
            ) : (
              <button onClick={submit} disabled={submitting} data-testid="booking-submit-btn" className="px-10 py-3.5 bg-primary text-primary-foreground text-sm tracking-wide hover:bg-primary/90 transition-colors clip-frame flex items-center gap-2 disabled:opacity-60">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Shield className="w-4 h-4" />}
                Pay deposit &amp; confirm
              </button>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
